import {
  inject,
  Injectable,
  signal,
  computed,
  DestroyRef,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { HttpClient } from "@angular/common/http";
import {
  filter,
  distinctUntilKeyChanged,
  switchMap,
  catchError,
  EMPTY,
  tap,
  map,
} from "rxjs";
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from "@azure/msal-angular";
import {
  InteractionStatus,
  RedirectRequest,
  AccountInfo,
  AuthenticationResult,
} from "@azure/msal-browser";

import { ApiResponse } from "@lib/types";

export interface UserRole {
  id: number;
  name: string;
}

export interface UserProfile {
  id: number;
  email: string;
  displayName: string;
  roles?: UserRole[];
}

export const ATTORNEY_ROLE_NAME = "attorney";

@Injectable({ providedIn: "root" })
export class AuthService {
  readonly account = signal<AccountInfo | null>(null);
  readonly userProfile = signal<UserProfile | null>(null);

  readonly isAttorneyOnly = computed(() => {
    const roles = this.userProfile()?.roles ?? [];
    if (roles.length !== 1) return false;
    return roles[0]?.name?.trim().toLocaleLowerCase() === ATTORNEY_ROLE_NAME;
  });

  private readonly msalGuardConfig =
    inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.initRedirectHandler();
    this.initAccountSync();
    this.initUserProvisioning();
  }

  private initRedirectHandler(): void {
    this.msalService
      .handleRedirectObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: AuthenticationResult | null) => {
          if (result?.account) {
            this.msalService.instance.setActiveAccount(result.account);
            this.account.set(result.account);
          }
        },
        error: (err) => console.error("Redirect handling failed", err),
      });
  }

  private initAccountSync(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.resolveActiveAccount());
  }

  private resolveActiveAccount(): void {
    const active = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();

    if (!active && accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      this.account.set(accounts[0]);
    } else {
      this.account.set(active);
    }
  }

  private initUserProvisioning(): void {
    toObservable(this.account)
      .pipe(
        filter(Boolean),
        distinctUntilKeyChanged("localAccountId"),
        switchMap(() =>
          this.http.post<ApiResponse<UserProfile>>("/api/users", {}).pipe(
            map((response) => {
              if (!response.success) {
                throw new Error(response.message ?? "JIT provisioning failed");
              }
              return response.data as UserProfile;
            }),
            tap((profile) => this.userProfile.set(profile)),
            catchError((err) => {
              console.error("JIT provisioning failed", err);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loginRedirect(): void {
    const request = this.msalGuardConfig.authRequest
      ? ({ ...this.msalGuardConfig.authRequest } as RedirectRequest)
      : undefined;

    this.msalService.loginRedirect(request);
  }

  logout(): void {
    this.userProfile.set(null);
    this.msalService.logoutRedirect({ postLogoutRedirectUri: "/login" });
  }
}
